import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { SelectData } from "../../../../models";
import { getWorkflowTemplate, GetWorkflowTemplateResponse, searchWorkflowTemplates } from "../../../../service/query";
import { Status } from "../../../../service/Shared";
import { updateWorkflowStageDates } from "../../../../utils";
import { Toast } from "../../../../widget";
import { getWorkflowTemplateExpiryFilterGroups } from "../../../shared/workflows";
import { NewDealJourneyContext } from "../NewDealJourneyContext";
import { UseDealWorkflowReturnData } from "./models/UseDealWorkflowReturnData";
import { getInitialStageDates, getOrderedDealPhases } from "./utils/dealWorkflowUtils";

export const useDealWorkflow = (): UseDealWorkflowReturnData => {
  const { errors, formValues, setFormValues } = useContext(NewDealJourneyContext);

  const [workflowTemplateOptions, setWorkflowTemplateOptions] = useState<SelectData>([]);
  const [workflowTemplate, setWorkflowTemplate] = useState<GetWorkflowTemplateResponse>();
  const [isLoadingWorkflowTemplate, setIsLoadingWorkflowTemplate] = useState(false);

  const orderedDealPhases = useMemo(() => getOrderedDealPhases(workflowTemplate?.phases), [workflowTemplate?.phases]);

  const loadWorkflowTemplate = useCallback(
    async (workflowTemplateUuid: string): Promise<void> => {
      setIsLoadingWorkflowTemplate(true);
      const response = await getWorkflowTemplate({ workflowTemplateUuid });
      setIsLoadingWorkflowTemplate(false);

      if (response.status !== Status.Success || !response.data) {
        throw new Error("Unable to load workflow template");
      }

      const nextOrderedDealPhases = getOrderedDealPhases(response.data.phases);

      setWorkflowTemplate(response.data);
      setFormValues((previousValues) => ({
        ...previousValues,
        dealWorkflowStageDates: getInitialStageDates(nextOrderedDealPhases, previousValues.dealWorkflowStageDates),
        dealWorkflowTemplateUuid: workflowTemplateUuid,
      }));
    },
    [setFormValues]
  );

  const handleWorkflowTemplateChange = useCallback(
    async (workflowTemplateUuid: string): Promise<void> => {
      if (!workflowTemplateUuid) {
        setWorkflowTemplate(undefined);
        setFormValues((previousValues) => ({
          ...previousValues,
          dealWorkflowStageDates: {},
          dealWorkflowTemplateUuid: undefined,
        }));
        return;
      }

      await loadWorkflowTemplate(workflowTemplateUuid);
    },
    [loadWorkflowTemplate, setFormValues]
  );

  const handleStageDateChange = useCallback(
    (phase: string, keyDate: string, value: Date | undefined): void => {
      setFormValues((previousValues) => ({
        ...previousValues,
        dealWorkflowStageDates: updateWorkflowStageDates(
          previousValues.dealWorkflowStageDates,
          orderedDealPhases,
          phase,
          keyDate,
          value
        ),
      }));
    },
    [orderedDealPhases, setFormValues]
  );

  const searchDealWorkflowTemplates = useCallback(async (): Promise<void> => {
    const response = await searchWorkflowTemplates({
      ...getWorkflowTemplateExpiryFilterGroups(false),
      paging: {
        limit: 50,
        beforeCursor: null,
        afterCursor: null,
      },
      sort: [
        {
          key: "results.name",
          direction: "asc",
        },
      ],
      filter: {
        results: {
          type: {
            operator: "eq",
            value: "Deal",
          },
        },
      },
    });

    if (response.status !== Status.Success || !response.data) {
      setWorkflowTemplateOptions([]);
      return;
    }

    setWorkflowTemplateOptions(response.data.results.map((item) => ({ key: item.uuid, value: item.name })));
  }, []);

  useEffect(() => {
    const loadWorkflowTemplates = async (): Promise<void> => {
      try {
        await searchDealWorkflowTemplates();
      } catch (error) {
        Toast.error({ message: error instanceof Error ? error.message : "Unable to load workflow templates" });
      }
    };

    loadWorkflowTemplates();
  }, [searchDealWorkflowTemplates]);

  useEffect(() => {
    const selectedWorkflowTemplateUuid = formValues.dealWorkflowTemplateUuid;

    if (!selectedWorkflowTemplateUuid) {
      setWorkflowTemplate(undefined);
      return;
    }

    const loadSelectedWorkflowTemplate = async (): Promise<void> => {
      try {
        await loadWorkflowTemplate(selectedWorkflowTemplateUuid);
      } catch (error) {
        Toast.error({ message: error instanceof Error ? error.message : "Unable to load workflow template" });
      }
    };

    loadSelectedWorkflowTemplate();
  }, [formValues.dealWorkflowTemplateUuid, loadWorkflowTemplate]);

  return {
    currentPageErrors: errors,
    formValues,
    handleStageDateChange,
    handleWorkflowTemplateChange: (workflowTemplateUuid: string) => {
      handleWorkflowTemplateChange(workflowTemplateUuid).catch((error) => {
        Toast.error({ message: error instanceof Error ? error.message : "Unable to load workflow template" });
      });
    },
    isLoadingWorkflowTemplate,
    workflowTemplateOptions,
    orderedDealPhases,
  };
};
